import { useState } from 'react';
import CampoTexto from '@/components/base/CampoTexto';
import CampoArea from '@/components/base/CampoArea';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import SeletorValor from './SeletorValor';
import BlocoVagasSuplentes from './BlocoVagasSuplentes';
import { urlEventoComCodigo } from '../utils';
import type { FormaParticipar, Grupo } from '../types';

interface CartaoParticipacaoProps {
  participacao: FormaParticipar;
  slug: string;
  ehIngresso: boolean;
  indice: number;
  expandido: boolean;
  erroNome?: string;
  erroCodigo?: string;
  erroVagas?: string;
  erroGrupos?: string;
  erroGrupoNome: (gi: number) => string | undefined;
  aoAlternar: () => void;
  aoMudar: (patch: Partial<FormaParticipar>) => void;
  aoMudarGrupo: (gi: number, patch: Partial<Grupo>) => void;
  aoAdicionarGrupo: () => void;
  aoRemoverGrupo: (gi: number) => void;
  aoRemover: () => void;
  /** Liga/desliga a validação presencial (só existe em modalidade já salva). */
  aoMudarPresencial?: (modalidadeId: number, ligado: boolean) => Promise<void>;
  /** Sugestão de abertura do lote (não grava sozinho — a direção confirma). */
  sugestaoAbertura?: { data: string; aviso: string } | null;
  aoAplicarSugestao?: () => void;
}

function doisDigitos(n: number): string {
  return String(n).padStart(2, '0');
}

function formatarQuando(data: string, hora: string): string {
  const d = new Date(`${data}T${hora || '00:00'}:00`);
  if (Number.isNaN(d.getTime())) return data;
  return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)} ${doisDigitos(
    d.getHours(),
  )}:${doisDigitos(d.getMinutes())}`;
}

function resumoVenda(p: FormaParticipar): string {
  const abrem = p.inscricoesAbremData
    ? formatarQuando(p.inscricoesAbremData, p.inscricoesAbremHora)
    : null;
  const fecham = p.inscricoesFechamData
    ? formatarQuando(p.inscricoesFechamData, p.inscricoesFechamHora)
    : null;
  if (!abrem && !fecham) return 'À venda desde já, sem data para fechar';
  if (!abrem) return `À venda desde já até ${fecham}`;
  if (!fecham) return `À venda de ${abrem}, sem data para fechar`;
  return `À venda de ${abrem} até ${fecham}`;
}

export default function CartaoParticipacao({
  participacao: p,
  slug,
  ehIngresso,
  indice,
  expandido,
  erroNome,
  erroCodigo,
  erroVagas,
  erroGrupos,
  erroGrupoNome,
  aoAlternar,
  aoMudar,
  aoMudarGrupo,
  aoAdicionarGrupo,
  aoRemoverGrupo,
  aoRemover,
  aoMudarPresencial,
  sugestaoAbertura = null,
  aoAplicarSugestao,
}: CartaoParticipacaoProps) {
  const [copiado, setCopiado] = useState(false);
  const [salvandoPresencial, setSalvandoPresencial] = useState(false);
  const [erroPresencial, setErroPresencial] = useState<string | null>(null);

  async function alternarPresencial() {
    if (p.id === null || !aoMudarPresencial || salvandoPresencial) return;
    const ligado = !p.validacaoPresencial;
    setSalvandoPresencial(true);
    setErroPresencial(null);
    try {
      await aoMudarPresencial(p.id, ligado);
      aoMudar({ validacaoPresencial: ligado });
    } catch {
      setErroPresencial('Não foi possível mudar agora. Tente de novo.');
    } finally {
      setSalvandoPresencial(false);
    }
  }

  const abreAoPublicar = !p.inscricoesAbremData;
  const somaGrupos = p.grupos.reduce((acc, g) => acc + (g.vagas ?? 0), 0);
  const estourou = p.temLimite && p.dividirGrupos && p.vagas !== null && somaGrupos > p.vagas;
  // Só mostra a configuração de suplentes quando o tipo tem vagas definidas.
  const temVagasDefinidas =
    (p.temLimite && p.vagas !== null && p.vagas > 0) || somaGrupos > 0;

  function abrirDatas() {
    if (!expandido) aoAlternar();
    setTimeout(() => {
      document
        .getElementById(`datas-part-${indice}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  }

  async function copiarLink() {
    try {
      await navigator.clipboard.writeText(urlEventoComCodigo(slug, p.codigoAcesso));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* ignora */
    }
  }

  return (
    <div className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-2 border-b border-secondary-100 px-4 py-2.5">
        <span className="flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
            {indice + 1}
          </span>
          <span className="text-sm font-medium text-foreground-900">
            {ehIngresso ? 'Ingresso' : 'Tipo de inscrição'} {indice + 1}
          </span>
        </span>
        <button
          type="button"
          onClick={aoRemover}
          aria-label={ehIngresso ? 'Apagar este ingresso' : 'Apagar este tipo de inscrição'}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-danger-50 hover:text-danger-600"
        >
          <i className="ri-close-line text-lg" />
        </button>
      </div>

      {/* Campos sempre visíveis */}
      <div className="space-y-5 px-4 py-5">
        <CampoTexto
          id={`part-nome-${indice}`}
          name="nome"
          label="Nome"
          help={
            ehIngresso
              ? 'Ex.: 1º Lote, 2º Lote, Inteira, Meia. Aparece para quem compra.'
              : 'Ex.: Campista, Equipe, Casal, Inteira. Aparece para quem se inscreve.'
          }
          value={p.nome}
          onChange={(v) => aoMudar({ nome: v })}
          required
          error={erroNome || null}
          placeholder={ehIngresso ? '1º Lote' : 'Casal'}
        />

        <div>
          <SeletorValor
            modo={p.valorModo}
            onChange={(valorModo) => aoMudar({ valorModo })}
            label={ehIngresso ? 'Valor do ingresso' : 'Valor da inscrição'}
          />
          {p.valorModo === 'pago' && !p.dividirGrupos && (
            <div className="mt-3 max-w-[320px]">
              <CampoDinheiro
                id={`valor-${indice}`}
                name="valor"
                label="Valor"
                help={
                  ehIngresso
                    ? 'Quanto custa este ingresso.'
                    : 'Quanto custa se inscrever nesta opção.'
                }
                centavos={p.valorCentavos}
                onChange={(valorCentavos) => aoMudar({ valorCentavos })}
              />
            </div>
          )}
        </div>

        <CampoTexto
          id={`vagas-${indice}`}
          name="vagas"
          label="Vagas"
          help={
            p.dividirGrupos
              ? 'Com divisão em grupos, este número vira o teto geral. Ex.: 100 no total.'
              : 'Quantas pessoas cabem. Deixe vazio se não tiver limite.'
          }
          value={p.vagas === null ? '' : String(p.vagas)}
          onChange={(v) =>
            aoMudar({
              vagas: v === '' ? null : Math.max(0, parseInt(v, 10) || 0),
              temLimite: v !== '',
            })
          }
          error={erroVagas || null}
          inputMode="numeric"
          placeholder="120"
        />

        {abreAoPublicar && (
          <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
              <i className="ri-information-line" />
            </span>
            <p className="text-sm text-foreground-800">
              As inscrições abrem assim que você publicar.
            </p>
          </div>
        )}

        {ehIngresso && (
          <>
            <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-foreground-600">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-calendar-line" />
              </span>
              <span>{resumoVenda(p)}</span>
              <button
                type="button"
                onClick={abrirDatas}
                className="cursor-pointer font-medium text-primary-600 underline transition-colors hover:text-primary-700"
              >
                alterar
              </button>
            </div>

            {sugestaoAbertura && aoAplicarSugestao && (
              <div className="rounded-md border border-warning-300 bg-warning-50 px-3 py-2.5">
                <p className="text-sm text-foreground-800">{sugestaoAbertura.aviso}</p>
                <button
                  type="button"
                  onClick={aoAplicarSugestao}
                  className="mt-1.5 cursor-pointer whitespace-nowrap text-sm font-medium text-primary-700 underline"
                >
                  Usar esta data
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mais opções */}
      <button
        type="button"
        onClick={aoAlternar}
        className="flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap border-t border-secondary-100 px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-50 hover:text-foreground-800"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className={expandido ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </span>
        {expandido ? 'Menos opções' : 'Mais opções'}
      </button>

      {expandido && (
        <div className="space-y-5 border-t border-secondary-100 px-4 py-5">
          <CampoArea
            id={`part-desc-${indice}`}
            name="descricao"
            label="Descrição"
            help="Explique rapidamente para quem é esta opção e o que ela inclui."
            value={p.descricao}
            onChange={(v) => aoMudar({ descricao: v })}
          />

          <div id={`datas-part-${indice}`}>
            <span className="block text-base font-medium text-foreground-950">
              {ehIngresso ? 'Quando este ingresso é vendido' : 'Inscrições abrem e fecham'}
            </span>
            <p className="mt-1 text-sm text-secondary-500">
              Se deixar o campo de abertura vazio, abre assim que publicar. Você pode publicar o
              evento antes das inscrições abrirem.
            </p>

            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-foreground-900">Abrem em</label>
                <div className="mt-1 flex gap-2">
                  <input
                    type="date"
                    value={p.inscricoesAbremData}
                    onChange={(e) => aoMudar({ inscricoesAbremData: e.target.value })}
                    className="w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
                  />
                  <input
                    type="time"
                    value={p.inscricoesAbremHora}
                    onChange={(e) => aoMudar({ inscricoesAbremHora: e.target.value })}
                    className="w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-900">Fecham em</label>
                <div className="mt-1 flex gap-2">
                  <input
                    type="date"
                    value={p.inscricoesFechamData}
                    onChange={(e) => aoMudar({ inscricoesFechamData: e.target.value })}
                    className="w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
                  />
                  <input
                    type="time"
                    value={p.inscricoesFechamHora}
                    onChange={(e) => aoMudar({ inscricoesFechamHora: e.target.value })}
                    className="w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {p.id !== null && temVagasDefinidas && (
            <BlocoVagasSuplentes modalidadeId={p.id} temVagas={temVagasDefinidas} />
          )}

          {p.id !== null && aoMudarPresencial && (
            <div className="rounded-md border border-secondary-200 bg-background-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <span className="block text-base font-medium text-foreground-950">
                    Inscrição presencial (vale a ordem da bipagem)
                  </span>
                  <p className="mt-1 text-sm text-secondary-500">
                    A pessoa preenche a ficha no celular e mostra o QR. A vaga é de quem a direção
                    bipar primeiro — quem recebe o link fora do local não fura a fila.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={p.validacaoPresencial}
                  aria-label={
                    p.validacaoPresencial
                      ? 'Desligar a inscrição presencial'
                      : 'Ligar a inscrição presencial'
                  }
                  onClick={alternarPresencial}
                  disabled={salvandoPresencial}
                  className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    p.validacaoPresencial ? 'bg-primary-500' : 'bg-secondary-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                      p.validacaoPresencial ? 'left-6' : 'left-1'
                    }`}
                  />
                </button>
              </div>
              {erroPresencial && (
                <p className="mt-2 text-sm text-danger-600" role="alert">
                  {erroPresencial}
                </p>
              )}
            </div>
          )}

          <div>
            <span className="block text-base font-medium text-foreground-950">
              Quem pode ver esta opção? <span className="text-danger-600">*</span>
            </span>
            <div className="mt-1">
              <OpcaoSelecao tipo="radio" checked={p.publica} onChange={() => aoMudar({ publica: true })}>
                Todo mundo — aparece na página do evento
              </OpcaoSelecao>
              <OpcaoSelecao
                tipo="radio"
                checked={!p.publica}
                onChange={() => aoMudar({ publica: false, codigoAcesso: p.codigoAcesso || '' })}
              >
                Só quem receber o link — não aparece na página, entra por link com código
              </OpcaoSelecao>
            </div>
          </div>

          {!p.publica && (
            <div className="rounded-lg border border-primary-200 bg-primary-50/50 p-4">
              <CampoTexto
                id={`part-codigo-${indice}`}
                name="codigo"
                label="Código de acesso"
                help="Quem tiver este código consegue abrir esta inscrição. Escolha algo fácil de digitar, sem acento."
                value={p.codigoAcesso}
                onChange={(v) => aoMudar({ codigoAcesso: v.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                required
                error={erroCodigo || null}
                placeholder="EQUIPE2026"
              />

              <div className="mt-3 rounded-md bg-white px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <code className="min-w-0 truncate font-mono text-xs text-foreground-800">
                    {urlEventoComCodigo(slug, p.codigoAcesso || 'EQUIPE2026')}
                  </code>
                  <button
                    type="button"
                    onClick={copiarLink}
                    className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                  >
                    <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} />
                    {copiado ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
                <p className="mt-1 text-xs text-secondary-500">É este link que você manda no grupo.</p>
              </div>
            </div>
          )}

          <div>
            <span className="block text-base font-medium text-foreground-950">
              Dividir as vagas em grupos?
            </span>
            <p className="mt-1 text-sm text-secondary-500">
              Use quando quiser reservar quantidades ou valores diferentes para públicos
              diferentes. Ex.: 50 masculinas e 50 femininas; inteira e meia.
            </p>
            <div className="mt-1">
              <OpcaoSelecao
                tipo="radio"
                checked={!p.dividirGrupos}
                onChange={() => aoMudar({ dividirGrupos: false })}
              >
                Não, é tudo uma coisa só
              </OpcaoSelecao>
              <OpcaoSelecao
                tipo="radio"
                checked={p.dividirGrupos}
                onChange={() => aoMudar({ dividirGrupos: true })}
              >
                Sim, quero dividir
              </OpcaoSelecao>
            </div>
          </div>

          {p.dividirGrupos && (
            <div className="space-y-3">
              {p.grupos.map((g, gi) => (
                <div key={gi} className="rounded-md border border-secondary-200 bg-background-50 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground-800">Grupo {gi + 1}</span>
                    <button
                      type="button"
                      onClick={() => aoRemoverGrupo(gi)}
                      aria-label="Remover grupo"
                      className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-danger-600"
                    >
                      <i className="ri-close-line text-lg" />
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <CampoTexto
                      id={`grupo-${indice}-${gi}-nome`}
                      name="nome"
                      label="Nome"
                      help="Ex.: Masculino, Feminino, Estudante, Meia-entrada."
                      value={g.nome}
                      onChange={(v) => aoMudarGrupo(gi, { nome: v })}
                      required
                      error={erroGrupoNome(gi) || null}
                      placeholder="Masculino"
                    />
                    <CampoTexto
                      id={`grupo-${indice}-${gi}-vagas`}
                      name="vagas"
                      label="Vagas"
                      value={g.vagas === null ? '' : String(g.vagas)}
                      onChange={(v) =>
                        aoMudarGrupo(gi, {
                          vagas: v === '' ? null : Math.max(0, parseInt(v, 10) || 0),
                        })
                      }
                      inputMode="numeric"
                      placeholder="50"
                    />
                  </div>
                  {p.valorModo === 'pago' && (
                    <div className="mt-3 max-w-[320px]">
                      <CampoDinheiro
                        id={`grupo-${indice}-${gi}-valor`}
                        name="valor"
                        label="Valor"
                        centavos={g.valorCentavos}
                        onChange={(valorCentavos) => aoMudarGrupo(gi, { valorCentavos })}
                      />
                    </div>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={aoAdicionarGrupo}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-add-line" />
                </span>
                Adicionar grupo
              </button>

              {estourou && (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  A soma dos grupos ({somaGrupos}) passa do limite geral ({p.vagas}).
                </p>
              )}
              {erroGrupos && (
                <p className="text-sm text-danger-600" role="alert" data-erro="true">
                  {erroGrupos}
                </p>
              )}
            </div>
          )}

          {ehIngresso && (
            <>
              <div>
                <span className="block text-base font-medium text-foreground-950">
                  Cada ingresso tem nome e CPF?
                </span>
                <div className="mt-1">
                  <OpcaoSelecao
                    tipo="radio"
                    checked={p.exigeIdentificacao}
                    onChange={() => aoMudar({ exigeIdentificacao: true })}
                  >
                    Sim, cada ingresso pede nome e CPF
                  </OpcaoSelecao>
                  <OpcaoSelecao
                    tipo="radio"
                    checked={!p.exigeIdentificacao}
                    onChange={() => aoMudar({ exigeIdentificacao: false })}
                  >
                    Não, um ingresso vale para qualquer pessoa
                  </OpcaoSelecao>
                </div>
              </div>

              <div className="max-w-[320px]">
                <CampoTexto
                  id={`part-max-compra-${indice}`}
                  name="max_por_compra"
                  label="Máximo por compra"
                  help="Quantos ingressos a pessoa pode comprar de uma vez."
                  value={String(p.maxPorCompra)}
                  onChange={(v) =>
                    aoMudar({ maxPorCompra: v === '' ? 1 : Math.max(1, parseInt(v, 10) || 1) })
                  }
                  inputMode="numeric"
                  placeholder="10"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}